'use client';

import { ReactNode } from 'react';
import { FacetChoice } from '@akinon/next/types';
import { useRouter } from '@akinon/next/hooks';
import { usePathname, useSearchParams } from 'next/navigation';
import { Radio, Checkbox } from '../../../components';
import { Facet, WIDGET_TYPE } from '../../../types';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@theme/components/shadcn/accordion';
import { CATEGORY_FACET_KEY } from './category-filter-settings';
import { CategoryTreeFilter } from './category-tree-filter';
import {
  getActiveFilterLabels,
  getChoiceSearchKey,
  isChoiceSelected,
  isMultiSelectFacet,
  useTrackableFacets
} from '../facet-grouping';
import { pushFilterInteraction } from '@theme/utils/gtm';

const COMPONENT_TYPES = {
  [WIDGET_TYPE.category]: Radio,
  [WIDGET_TYPE.multiselect]: Checkbox,
  [WIDGET_TYPE.multiple_selection]: Checkbox
};

/** Ürün tarafında listelenmemesi istenen seçenekler. */
const EXCLUDED_CHOICE_LABELS = ['Robot Süpürge'];

interface Props {
  facet: Facet;
  isPending: boolean;
  tempSearchParams: URLSearchParams;
  setTempSearchParams: (params: URLSearchParams) => void;
  immediateApply?: boolean;
}

const getComponentByWidgetType = (widgetType: string) =>
  COMPONENT_TYPES[widgetType] || COMPONENT_TYPES[WIDGET_TYPE.category];

const FilterAccordionItem = ({
  facet,
  children
}: {
  facet: Facet;
  children: ReactNode;
}) => (
  <AccordionItem value={facet.key}>
    <AccordionTrigger
      icons={['plus', 'minus']}
      iconSize={24}
      className="text-sm py-4.5"
      removeHeading
    >
      {facet.name}
    </AccordionTrigger>
    <AccordionContent>{children}</AccordionContent>
  </AccordionItem>
);

export const FilterItem = ({
  facet,
  isPending,
  tempSearchParams,
  setTempSearchParams,
  immediateApply
}: Props) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const facets = useTrackableFacets();

  /** Tek seçimli kategori facet'i: aynı değere tekrar tıklamak seçimi kaldırır. */
  const handleSelectCategory = (choice: FacetChoice) => {
    const value = String(choice.value);
    const isSelected = tempSearchParams.get(facet.search_key) === value;

    const toggle = (params: URLSearchParams) => {
      if (isSelected) {
        params.delete(facet.search_key);
      } else {
        params.set(facet.search_key, value);
      }

      return params;
    };

    const nextParams = toggle(new URLSearchParams(tempSearchParams.toString()));

    setTempSearchParams(nextParams);
    pushFilterInteraction({
      action: isSelected ? 'deselect' : 'select',
      category: facet.name,
      value: choice.label,
      activeFilters: getActiveFilterLabels(facets, nextParams)
    });

    if (immediateApply) {
      const applied = toggle(new URLSearchParams(searchParams.toString()));

      router.push(pathname + '?' + applied.toString(), { scroll: false });
    }
  };

  const isMultiSelect = isMultiSelectFacet(facet);

  /**
   * Seçenek bileşenine hem `onChange` hem `onClick` bağlı (radio'da seçiliyi
   * tekrar tıklayarak kaldırabilmek için `onClick` gerekli), bu yüzden tek
   * tıklamada iki kez koşuyor. İkisi de aynı `tempSearchParams`'ı okuduğu için
   * filtreleme etkilenmiyor; event ise her tıklamada tam bir kez çalışan
   * `onClick`'ten basılsın diye `shouldTrack` ile ayrıldı.
   */
  const handleSelectFilter = (choice: FacetChoice, shouldTrack = false) => {
    const searchKey = getChoiceSearchKey(facet, choice);
    const value = String(choice.value);
    const newParams = new URLSearchParams(tempSearchParams.toString());
    const wasSelected = isChoiceChecked(choice);

    if (!isMultiSelect) {
      if (tempSearchParams.get(searchKey) === value) {
        newParams.delete(searchKey);
      } else {
        newParams.set(searchKey, value);
      }
    } else {
      const values = tempSearchParams.getAll(searchKey);

      if (values.includes(value)) {
        newParams.delete(searchKey);
        values
          .filter((current) => current !== value)
          .forEach((current) => newParams.append(searchKey, current));
      } else {
        newParams.append(searchKey, value);
      }
    }

    setTempSearchParams(newParams);

    if (shouldTrack) {
      pushFilterInteraction({
        action: wasSelected ? 'deselect' : 'select',
        category: facet.name,
        value: choice.label,
        activeFilters: getActiveFilterLabels(facets, newParams)
      });
    }
  };

  const isChoiceChecked = (choice: FacetChoice) =>
    isChoiceSelected(facet, choice, tempSearchParams);

  const choices = facet?.data?.choices || [];

  if (facet.key === CATEGORY_FACET_KEY) {
    return (
      <FilterAccordionItem facet={facet}>
        <CategoryTreeFilter
          choices={choices}
          selectedValue={tempSearchParams.get(facet.search_key)}
          isPending={isPending}
          onSelect={handleSelectCategory}
        />
      </FilterAccordionItem>
    );
  }

  const Component = getComponentByWidgetType(facet.widget_type);

  return (
    <FilterAccordionItem facet={facet}>
      <div>
        {choices
          .filter((choice) => !EXCLUDED_CHOICE_LABELS.includes(choice.label))
          .map((choice, index) => (
            <Component
              key={choice.label}
              data={choice}
              name={facet.key}
              onChange={() => handleSelectFilter(choice)}
              onClick={() => handleSelectFilter(choice, true)}
              checked={isChoiceChecked(choice)}
              data-testid={`${choice.label.trim()}`}
              disabled={isPending}
              className="flex flex-row items-center justify-start h-8 text-sm"
            >
              {choice.label} (
              <span
                data-testid={`filter-count-${facet.name.toLowerCase()}-${index}`}
              >
                {choice.quantity}
              </span>
              )
            </Component>
          ))}
      </div>
    </FilterAccordionItem>
  );
};
